import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Target, Zap, RefreshCw, X } from "lucide-react";
import Section from "./Section";
import "./ServicesSection.css";

const ServicesSection = () => {
  const [selectedService, setSelectedService] = useState(null);

  const services = [
    {
      icon: <Target size={32} />,
      title: "Precision Marketing",
      description:
        "Data isn't just a report; it's our fuel. Every decision is calculated for maximum ROI.",
      color: "#FFD700", // Yellow color
    },
    {
      icon: <Zap size={32} />,
      title: "Creative Velocity",
      description:
        "Design that moves fast and hits hard. We create visuals that stop the scroll and start the conversation.",
      color: "#FFD700", // Yellow color
    },
    {
      icon: <RefreshCw size={32} />,
      title: "Agile Execution",
      description:
        "The market changes daily. We adapt instantly. No sidetracking, just forward motion.",
      color: "#FFD700", // Yellow color
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.6, type: "spring", stiffness: 100 },
    },
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3, ease: "easeOut" },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: { duration: 0.2, ease: "easeIn" },
    },
  };

  const handleOpenModal = (service) => {
    setSelectedService(service);
  };

  const handleCloseModal = () => {
    setSelectedService(null);
  };

  return (
    <Section id="services" className="services-section">
      <div className="services-content">
        <h2 className="services-title">
          We Turn <span className="highlight">Potential Energy</span> into{" "}
          <span className="highlight">Market Momentum</span>.
        </h2>
        <p className="services-subtitle">
          Kynetic marketing agency is a growth-obsessed agency blending data
          precision with creative force. We don't just launch campaigns; we
          engineer trajectories for scalable success.
        </p>

        <motion.div
          className="services-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              className="service-card"
              variants={itemVariants}
              whileHover={{ y: -10, transition: { duration: 0.2 } }}
              onClick={() => handleOpenModal(service)}
            >
              <div className="service-icon" style={{ color: service.color }}>
                {service.icon}
              </div>
              <h3 className="service-card-title">{service.title}</h3>
              <div className="card-overlay">
                <span>Click to learn more</span>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {selectedService && (
          <motion.div
            className="modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseModal}
          >
            <motion.div
              className="modal-content"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
            >
              <button className="modal-close" onClick={handleCloseModal}>
                <X size={24} />
              </button>
              <div
                className="modal-icon"
                style={{ color: selectedService.color }}
              >
                {selectedService.icon}
              </div>
              <h3 className="modal-title">{selectedService.title}</h3>
              <p className="modal-description">{selectedService.description}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Section>
  );
};

export default ServicesSection;
